import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import logoFornbo from '../logo_fornbo.jpg';
import './Nav.css';

export default function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  // Listen for section change events from Home.jsx IntersectionObserver
  useEffect(() => {
    const handleSectionChange = (e) => {
      setActiveSection(e.detail);
    };
    window.addEventListener('sectionChange', handleSectionChange);
    return () => window.removeEventListener('sectionChange', handleSectionChange);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  // Determine if a nav item is active based on scroll position or URL hash
  const isActive = (path) => {
    const hash = path.startsWith('/#') ? path.slice(2) : '';
    // If we're on home page and have an active section from scrolling, use that
    if (location.pathname === '/' && activeSection) {
      return activeSection === hash;
    }
    // Fall back to URL-based matching
    return location.pathname === path || location.hash === path;
  };
  const isDashboard = location.pathname.startsWith('/styrelsen/dashboard');

  return (
    <>
      <nav className="nav">
        <Link to="/" className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="nav-logo-icon">
            <img src={logoFornbo} alt="Fornbo logotyp" />
          </div>
          <div className="nav-logo-text">
            Fornbo
            <span className="nav-logo-subtitle">Samfällighetsförening</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <ul className="nav-menu-desktop">
          <li><Link to="/#aktuellt" className={`nav-link ${isActive('/#aktuellt') ? 'nav-link-active' : ''}`}>Aktuellt</Link></li>
          <li><Link to="/#ansvarsomraden" className={`nav-link ${isActive('/#ansvarsomraden') ? 'nav-link-active' : ''}`}>Ansvarsområden</Link></li>
          <li><Link to="/#sjon" className={`nav-link ${isActive('/#sjon') ? 'nav-link-active' : ''}`}>Sjön Nedingen</Link></li>
          <li><Link to="/#foreningen" className={`nav-link ${isActive('/#foreningen') ? 'nav-link-active' : ''}`}>Föreningen</Link></li>
          <li><Link to="/#dokument" className={`nav-link ${isActive('/#dokument') ? 'nav-link-active' : ''}`}>Dokument</Link></li>
          <li><Link to="/#kontakt" className={`nav-link ${isActive('/#kontakt') ? 'nav-link-active' : ''}`}>Kontakt</Link></li>
          
          <li className="nav-user-item" ref={userMenuRef}>
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  className={`nav-link nav-user-button ${showUserMenu || isDashboard ? 'nav-link-active' : ''}`}
                >
                  <span>🔓</span>
                  <span className="nav-username">{user?.username || 'Styrelsen'}</span>
                  <span className="nav-chevron">▼</span>
                </button>

                {showUserMenu && (
                  <div className="nav-user-dropdown">
                    <div className="nav-user-header">
                      <div className="nav-user-name">{user?.username}</div>
                      <div className="nav-user-role">
                        {{ admin: 'Administratör', contentCreator: 'Redaktör', sekreterare: 'Sekreterare', readOnly: 'Läsbehörighet' }[user?.role] || 'Läsbehörighet'}
                      </div>
                    </div>
                    <Link to="/styrelsen/dashboard" onClick={() => setShowUserMenu(false)} className="nav-dropdown-link">Dashboard</Link>
                    <Link to="/styrelsen/dashboard/profile" onClick={() => setShowUserMenu(false)} className="nav-dropdown-link">Min profil</Link>
                    <div className="nav-dropdown-divider">
                      <button onClick={() => { logout(); setShowUserMenu(false); navigate('/'); }} className="nav-logout-button">
                        Logga ut
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Link to="/styrelsen" className={`nav-link nav-link-bordered ${isActive('/styrelsen') ? 'nav-link-active' : ''}`}>
                🔒 Styrelsen
              </Link>
            )}
          </li>
        </ul>

        {/* Mobile Toggle Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Meny"
          className={`nav-toggle ${isOpen ? 'nav-toggle-open' : ''}`}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>

      {/* Mobile Menu */}
      <div className={`nav-mobile-menu ${isOpen ? 'nav-mobile-menu-open' : ''}`}>
        <ul className="nav-mobile-list">
          <li><Link to="/#aktuellt" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Aktuellt</Link></li>
          <li><Link to="/#ansvarsomraden" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Ansvarsområden</Link></li>
          <li><Link to="/#sjon" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Sjön Nedingen</Link></li>
          <li><Link to="/#foreningen" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Föreningen</Link></li>
          <li><Link to="/#dokument" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Dokument</Link></li>
          <li><Link to="/#kontakt" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Kontakt</Link></li>
          
          {isAuthenticated ? (
            <>
              <li className="nav-mobile-divider"></li>
              <li><Link to="/styrelsen/dashboard" className="nav-mobile-link nav-mobile-highlight" onClick={() => setIsOpen(false)}>🔓 Dashboard</Link></li>
              <li><Link to="/styrelsen/dashboard/profile" className="nav-mobile-link" onClick={() => setIsOpen(false)}>Min profil</Link></li>
              <li>
                <button 
                  onClick={() => { logout(); setIsOpen(false); navigate('/'); }}
                  className="nav-mobile-link nav-mobile-logout"
                >
                  Logga ut
                </button>
              </li>
            </>
          ) : (
            <li><Link to="/styrelsen" className="nav-mobile-link nav-mobile-highlight" onClick={() => setIsOpen(false)}>🔒 Styrelsen</Link></li>
          )}
        </ul>
      </div>

      {/* Mobile Overlay */}
      {isOpen && <div className="nav-overlay" onClick={() => setIsOpen(false)} />}
    </>
  );
}
